import cloneDeep from "lodash/cloneDeep";

import * as actionTypes from "../constants";
import { DomainType, USER_NO_ROLE, UserStatus } from "../models";
import { getDefaultProFeeValue } from "../utils/proFeeDefaults";

import type { FacilityEntity } from "scenes/Facility/models/facilities";
import type {
  DomainTypeEnum,
  EncoderUserEntity,
  FacilityRolesEntity,
  ManageEncoderUserState,
} from "../models";
import type { FacilityRolesUpdatedPayload } from "../scenes/Users/actions/updateFacilityRoles";

const createEmptyEncoderUser = (): EncoderUserEntity => ({
  id: "",
  clientId: "",
  customerId: "",
  firstName: "",
  lastName: "",
  userId: "",
  userName: "",
  email: "",
  isSystemAdmin: false,
  isItAdmin: false,
  status: UserStatus.DISABLED,
  version: "",
  roles: [],
  practiceRoles: [],
  defaultRole: USER_NO_ROLE,
});

const createEmptyManageEncoderUserState = (): ManageEncoderUserState => ({
  selectedAccount: createEmptyEncoderUser(),
  errorText: "",
  inviteErrorTexts: [],
  inviteSuccesses: [],
  loading: false,
  dirty: false,
  userInfoDirty: false,
});

export const manageEncoderUserReducer = (
  state = createEmptyManageEncoderUserState(),
  action: any,
) => {
  switch (action.type) {
    case actionTypes.SET_USERS_LOADING:
      return handleSetUsersLoading(state, action.payload);
    case actionTypes.GET_SELECTED_USER_BEGIN:
      return handleGetSelectedEncoderUserBegin(state);
    case actionTypes.CREATE_NEW_USER_BEGIN:
      return handleGetSelectedEncoderUserBegin();
    case actionTypes.GET_SELECTED_USER_ROLES_BEGIN:
      return handleGetSelectedUserRolesBegin(state);
    case actionTypes.GET_SELECTED_USER_COMPLETED:
      return handleGetSelectedEncoderUserCompleted(state, action.payload);
    case actionTypes.GET_SELECTED_USER_ERROR:
    case actionTypes.GET_SELECTED_USER_ROLES_ERROR:
    case actionTypes.SAVE_SELECTED_USER_ROLES_ERROR:
      return handleGetSelectedEncoderUserError(state, action.payload);
    case actionTypes.UPDATE_SELECTED_ENCODER_USER_FIELD:
      return handleUpdateEncoderUserField(state, action.payload);
    case actionTypes.INVITE_NEW_USER_BEGIN:
    case actionTypes.INVITE_MULTIPLE_NEW_USERS_BEGIN:
      return handleInviteUserBegin(state, action.payload);
    case actionTypes.GET_SELECTED_USER_ROLES_COMPLETED:
      return handleGetSelectedUserRolesCompleted(state, action.payload);
    case actionTypes.UPDATE_FACILITY_ROLES:
      return handleSelectedUserRolesChanged(state, action.payload);
    case actionTypes.SET_ALL_FACILITY_ROLES:
      return handleSelectedUserChangeAllRoles(state, action.payload);
    case actionTypes.CREATE_NEW_USER_COMPLETED:
      return handleCreateNewUserCompleted(state, action.payload);
    case actionTypes.SAVE_SELECTED_USER_ROLES_COMPLETED:
      return handleSaveSelectedUserRolesCompleted(state, action.payload);
    case actionTypes.REINVITE_USER_BEGIN:
      return handleReinviteUserBegin(state, action.payload);
    case actionTypes.INVITE_NEW_USER_COMPLETED:
      return handleInviteUserCompleted(state, action.payload, true);
    case actionTypes.DELETE_USER_INVITE_COMPLETED:
      return handleInviteUserCompleted(state, action.payload);
    case actionTypes.INVITE_MULTIPLE_NEW_USERS_COMPLETED:
      return handleInviteMultipleUserCompleted(state, action.payload);
    case actionTypes.INVITE_MULTIPLE_NEW_USERS_PROCESSING:
      return handleInviteMultipleUserProcessing(state, action.payload);
    case actionTypes.REINVITE_USER_COMPLETED:
      return handleReinviteUserCompleted(state, action.payload);
    case actionTypes.CANCEL_CREATE_NEW_USER_COMPLETED:
    case actionTypes.CREATE_NEW_ENCOUNTER:
      return createEmptyManageEncoderUserState();
    case actionTypes.CLEAR_INVITE_NEW_USER_ERROR:
      return handleClearInviteUserError(state);
    case actionTypes.INVITE_NEW_USER_ERROR:
      return handleInviteUserError(state, action.payload);
    case actionTypes.REINVITE_NEW_USER_ERROR:
      return handleReInviteUserError(state, action.payload);
    case actionTypes.MANAGE_ENCODER_USER_UPDATE_FACILITY_PROFEE:
      return handleUpdateFacilityProFee(state, action.payload);
    case actionTypes.MANAGE_ENCODER_USER_SET_ALL_FACILITIES_PROFEE:
      return handleSetAllFacilitiesProFee(state, action.payload);
    case actionTypes.SAVE_USER_INFO_ONLY_COMPLETED:
      return handleUpdateUserFromApiResponse(state, action.payload);
    case actionTypes.SAVE_USER_INFO_ONLY_ERROR:
      return handleSaveUserInfoError(state);
    case actionTypes.SET_USER_INFO_DIRTY:
      return handleSetUserInfoDirty(state, action.payload);
    // Optimistic update cases
    case actionTypes.SAVE_USER_ADMIN_SETTING_OPTIMISTIC_UPDATE:
      return handleAdminSettingOptimisticUpdate(state, action.payload);
    case actionTypes.SAVE_USER_ADMIN_SETTING_REVERT:
      return handleAdminSettingRevert(state, action.payload);
    case actionTypes.SAVE_SINGLE_FACILITY_ROLE_OPTIMISTIC_UPDATE:
      return handleOptimisticRoleUpdate(state, action.payload);
    case actionTypes.SET_ALL_FACILITY_ROLES_OPTIMISTIC_UPDATE:
      return handleOptimisticSetAllRoles(state, action.payload);
    case actionTypes.SAVE_SINGLE_FACILITY_PROFEE_OPTIMISTIC_UPDATE:
      return handleOptimisticProFeeUpdate(state, action.payload);
    case actionTypes.SET_ALL_FACILITIES_PROFEE_OPTIMISTIC_UPDATE:
      return handleOptimisticSetAllProFee(state, action.payload);
    default:
      return state;
  }
};

const handleUpdateEncoderUserField = (
  state: ManageEncoderUserState,
  payload: any,
): ManageEncoderUserState => {
  const userChanged =
    state.selectedAccount[payload.fieldName] !== payload.value ||
    state.selectedAccount.userId === "";
  if (!userChanged) {
    return state;
  }

  const selectedAccount = {
    ...state.selectedAccount,
    [payload.fieldName]: payload.value,
    errorText: "",
  };
  return {
    ...state,
    loading: false,
    dirty: true,
    selectedAccount,
  };
};

const handleGetSelectedEncoderUserBegin = (
  state?: ManageEncoderUserState,
): ManageEncoderUserState => ({
  selectedAccount: state?.selectedAccount ?? createEmptyEncoderUser(),
  errorText: "",
  loading: true,
  dirty: false,
  userInfoDirty: false,
});

const handleGetSelectedUserRolesBegin = (
  state: ManageEncoderUserState,
): ManageEncoderUserState => ({
  ...state,
  errorText: "",
});

const getRolesType = (domain: DomainTypeEnum) =>
  domain === DomainType.Practice ? "practiceRoles" : "roles";

const handleSelectedUserRolesChanged = (
  state: ManageEncoderUserState,
  payload: FacilityRolesUpdatedPayload,
): ManageEncoderUserState => {
  const selectedAccount = {
    ...state.selectedAccount,
    errorText: "",
  };

  const roleType = getRolesType(payload.domain);

  const newRoles = cloneDeep(state.selectedAccount[roleType]);
  const updatedFacility = newRoles?.find(
    (f: FacilityRolesEntity) => f.FacilityId === payload.facility,
  );

  if (updatedFacility) {
    const oldRole = updatedFacility.Role;
    updatedFacility.Role = payload.role;
    const isNewUser = !state.selectedAccount.userId;

    if (isNewUser && oldRole === "NoRole" && payload.role !== "NoRole" && state.facilities) {
      const facility = state.facilities.find((f) => f.ViewId === payload.facility);

      if (facility) {
        updatedFacility.AllowProfessionalCoding = getDefaultProFeeValue(
          facility.AllowProfessionalCoding,
        );
      }
    }
  }

  selectedAccount[roleType] = newRoles;
  return {
    ...state,
    dirty: true,
    loading: false,
    selectedAccount,
  };
};

const handleSelectedUserChangeAllRoles = (
  state: ManageEncoderUserState,
  payload: FacilityRolesUpdatedPayload,
): ManageEncoderUserState => {
  const selectedAccount = {
    ...state.selectedAccount,
    errorText: "",
  };
  const roleType = getRolesType(payload.domain);

  const isNewUser = !state.selectedAccount.userId;

  selectedAccount[roleType] = selectedAccount[roleType]?.map((el) => {
    const updatedRole = { ...el, Role: payload.role };

    if (
      payload.domain === DomainType.Facility &&
      isNewUser &&
      el.Role === "NoRole" &&
      payload.role !== "NoRole" &&
      state.facilities
    ) {
      const facility = state.facilities.find((f) => f.ViewId === el.FacilityId);

      if (facility) {
        updatedRole.AllowProfessionalCoding = getDefaultProFeeValue(
          facility.AllowProfessionalCoding,
        );
      }
    }

    return updatedRole;
  });

  return {
    ...state,
    dirty: true,
    loading: false,
    selectedAccount,
  };
};

const handleInviteUserBegin = (
  state: ManageEncoderUserState,
  _payload: EncoderUserEntity,
): ManageEncoderUserState => {
  const updatedState = {
    ...state,
    selectedAccount: state.selectedAccount,
    errorText: "",
    loading: true,
  };

  return updatedState;
};

const handleGetSelectedEncoderUserCompleted = (
  state: ManageEncoderUserState,
  payload: EncoderUserEntity,
): ManageEncoderUserState => {
  const isSameUser = state.selectedAccount.userId === payload.userId;

  // Preserve existing roles and facilities if they exist (prevents race condition where
  // GET_SELECTED_USER_COMPLETED arrives after GET_SELECTED_USER_ROLES_COMPLETED)
  const selectedAccount = {
    ...payload,
    roles:
      isSameUser && (state.selectedAccount.roles?.length ?? 0) > 0 ?
        state.selectedAccount.roles
      : payload.roles,
    practiceRoles:
      isSameUser && (state.selectedAccount.practiceRoles?.length ?? 0) > 0 ?
        state.selectedAccount.practiceRoles
      : payload.practiceRoles,
  };

  const updatedState: ManageEncoderUserState = {
    selectedAccount,
    errorText: "",
    dirty: false,
    loading: false,
    userInfoDirty: false,
  };

  // Only preserve facilities if they exist (don't set undefined)
  if (state.facilities && state.facilities.length > 0) {
    updatedState.facilities = state.facilities;
  }

  return updatedState;
};

const handleGetSelectedEncoderUserError = (
  state: ManageEncoderUserState,
  error: Error,
): ManageEncoderUserState => {
  const updatedState = {
    ...state,
    selectedAccount: createEmptyEncoderUser(),
    errorText: error.message,
    loading: false,
  };

  return updatedState;
};

const handleGetSelectedUserRolesCompleted = (
  state: ManageEncoderUserState,
  payload: {
    selectedUser: EncoderUserEntity;
    facilityRoles: FacilityRolesEntity[];
    practiceRoles: FacilityRolesEntity[];
    forceDirty: boolean;
  },
): ManageEncoderUserState => {
  const selectedAccount = {
    ...payload.selectedUser,
    roles: payload.facilityRoles,
    practiceRoles: payload.practiceRoles,
  };

  return {
    ...state,
    loading: false,
    dirty: payload.forceDirty,
    selectedAccount,
  };
};

const handleSaveSelectedUserRolesCompleted = (
  state: ManageEncoderUserState,
  payload: {
    isNewUser?: boolean;
    selectedUser: EncoderUserEntity;
  },
): ManageEncoderUserState => {
  const updatedUser = payload.isNewUser ? payload.selectedUser : state.selectedAccount;

  return {
    selectedAccount: updatedUser,
    errorText: "",
    dirty: false,
    loading: Boolean(payload.isNewUser),
    userInfoDirty: state.userInfoDirty,
  };
};

const handleCreateNewUserCompleted = (
  state: ManageEncoderUserState,
  payload: { facilities?: FacilityEntity[]; practices?: FacilityEntity[] },
): ManageEncoderUserState => {
  const newUserRoles: FacilityRolesEntity[] =
    payload.facilities?.map((facility: FacilityEntity) => {
      // For new users with "NoRole", set AllowProfessionalCoding based on facility settings
      // This will be the default when they change to an actual role
      const defaultProFee = getDefaultProFeeValue(facility.AllowProfessionalCoding);

      return {
        Facility: facility.Id,
        FacilityId: facility.ViewId,
        Role: "NoRole",
        UserId: "",
        Version: "",
        FacilityIsActive: facility.IsActive,
        // Store the default value, but it won't be displayed until they select a role
        AllowProfessionalCoding: defaultProFee,
      };
    }) ?? [];

  const newUserPracticeRoles: FacilityRolesEntity[] =
    payload.practices?.map((facility: FacilityEntity) => ({
      Facility: facility.Id,
      FacilityId: facility.ViewId,
      Role: "NoRole",
      UserId: "",
      Version: "",
      FacilityIsActive: facility.IsActive,
      AllowProfessionalCoding: false,
    })) ?? [];

  const selectedAccount = {
    ...state.selectedAccount,
    roles: newUserRoles,
    practiceRoles: newUserPracticeRoles,
  };

  return {
    selectedAccount,
    errorText: "",
    dirty: false,
    loading: false,
    userInfoDirty: false,
    facilities: payload.facilities,
    practices: payload.practices,
  };
};

const handleReinviteUserBegin = (
  state: ManageEncoderUserState,
  payload: EncoderUserEntity,
): ManageEncoderUserState => {
  const updatedState = {
    ...state,
    errorText: "",
    loading: true,
    dirty: false,
  };

  return updatedState;
};

const handleInviteMultipleUserProcessing = (
  state: ManageEncoderUserState,
  payload: EncoderUserEntity,
): ManageEncoderUserState => {
  const selectedAccount = {
    ...state.selectedAccount,
    email: payload.email,
  };

  const updatedState = {
    selectedAccount,
    errorText: "",
    dirty: true,
    loading: false,
    userInfoDirty: false,
  };

  return updatedState;
};

const handleInviteMultipleUserCompleted = (
  state: ManageEncoderUserState,
  payload: { lastInvitedUser: EncoderUserEntity; errors: string[]; successes: string[] },
): ManageEncoderUserState => {
  if (payload.errors.length > 0) {
    return {
      ...state,
      inviteErrorTexts: [...payload.errors],
      inviteSuccesses: [...payload.successes],
      dirty: false,
      loading: false,
    };
  }

  const selectedAccount = {
    ...state.selectedAccount,
    userId: undefined,
    customerId: undefined,
    roles: payload.lastInvitedUser?.roles || [],
    practiceRoles: payload.lastInvitedUser?.practiceRoles || [],
    status: UserStatus.DISABLED,
  };

  const updatedState = {
    selectedAccount,
    errorText: "",
    dirty: false,
    loading: false,
    userInfoDirty: false,
  };

  return updatedState;
};

const handleInviteUserCompleted = (
  state: ManageEncoderUserState,
  payload: EncoderUserEntity,
  loading = false,
): ManageEncoderUserState => {
  const updatedState = {
    selectedAccount: payload,
    errorText: "",
    dirty: false,
    loading,
    userInfoDirty: false,
  };

  return updatedState;
};

const handleReinviteUserCompleted = (
  state: ManageEncoderUserState,
  payload,
): ManageEncoderUserState => {
  const selectedAccount = {
    ...payload.selectedUser,
    roles: payload.roles,
    practiceRoles: payload.practiceRoles,
  };

  return {
    ...state,
    loading: false,
    dirty: false,
    selectedAccount,
  };
};

const handleClearInviteUserError = (state: ManageEncoderUserState): ManageEncoderUserState => {
  const updatedState = {
    selectedAccount: createEmptyEncoderUser(),
    errorText: state.errorText,
    inviteErrorTexts: [],
    inviteSuccesses: [],
    loading: false,
    dirty: false,
    userInfoDirty: false,
  };

  return updatedState;
};

const handleInviteUserError = (state: ManageEncoderUserState, payload): ManageEncoderUserState => {
  const updatedState = {
    selectedAccount: createEmptyEncoderUser(),
    errorText: payload,
    loading: false,
    dirty: false,
    userInfoDirty: false,
  };

  return updatedState;
};

const handleReInviteUserError = (
  state: ManageEncoderUserState,
  payload,
): ManageEncoderUserState => {
  const updatedState = {
    ...state,
    errorText: payload,
    loading: false,
  };

  return updatedState;
};

const handleSetUsersLoading = (state: ManageEncoderUserState, payload): ManageEncoderUserState => {
  const updatedState = {
    ...state,
    loading: payload,
  };

  return updatedState;
};

const handleUpdateFacilityProFee = (
  state: ManageEncoderUserState,
  payload: {
    userState: ManageEncoderUserState;
    facilityId: string;
    allowProfessionalCoding: boolean;
  },
): ManageEncoderUserState => {
  const selectedAccount = {
    ...state.selectedAccount,
    errorText: "",
  };

  const newRoles = cloneDeep(state.selectedAccount.roles);
  const updatedFacility = newRoles.find(
    (f: FacilityRolesEntity) => f.FacilityId === payload.facilityId,
  );

  if (updatedFacility) {
    updatedFacility.AllowProfessionalCoding = payload.allowProfessionalCoding;
  }

  selectedAccount.roles = newRoles;

  return {
    ...state,
    dirty: true,
    loading: false,
    selectedAccount,
  };
};

const handleSetAllFacilitiesProFee = (
  state: ManageEncoderUserState,
  payload: { userState: ManageEncoderUserState; allowProfessionalCoding: boolean },
): ManageEncoderUserState => {
  const selectedAccount = {
    ...state.selectedAccount,
    errorText: "",
  };

  selectedAccount.roles = selectedAccount.roles.map((el: FacilityRolesEntity) => ({
    ...el,
    AllowProfessionalCoding: payload.allowProfessionalCoding,
  }));

  return {
    ...state,
    dirty: true,
    loading: false,
    selectedAccount,
  };
};

// Optimistic update handlers
const handleOptimisticRoleUpdate = (
  state: ManageEncoderUserState,
  payload: { userId: string; facilityId: string; role: string; domain: DomainTypeEnum },
): ManageEncoderUserState => {
  const rolesType = getRolesType(payload.domain);

  const selectedAccount = { ...state.selectedAccount };
  const newRoles = cloneDeep(state.selectedAccount[rolesType]);
  const updatedFacility = newRoles?.find(
    (f: FacilityRolesEntity) => f.FacilityId === payload.facilityId,
  );

  if (updatedFacility) {
    updatedFacility.Role = payload.role;
  }

  selectedAccount[rolesType] = newRoles;
  return {
    ...state,
    selectedAccount,
  };
};

const handleOptimisticSetAllRoles = (
  state: ManageEncoderUserState,
  payload: { userId: string; role: string; domain: DomainTypeEnum },
): ManageEncoderUserState => {
  const rolesType = getRolesType(payload.domain);

  const selectedAccount = { ...state.selectedAccount };

  selectedAccount[rolesType] = selectedAccount[rolesType].map((el: FacilityRolesEntity) => ({
    ...el,
    Role: payload.role,
  }));

  return {
    ...state,
    selectedAccount,
  };
};

const handleOptimisticProFeeUpdate = (
  state: ManageEncoderUserState,
  payload: { userId: string; facilityId: string; allowProfessionalCoding: boolean },
): ManageEncoderUserState => {
  const selectedAccount = { ...state.selectedAccount };
  const newRoles = cloneDeep(state.selectedAccount.roles);
  const updatedFacility = newRoles.find(
    (f: FacilityRolesEntity) => f.FacilityId === payload.facilityId,
  );

  if (updatedFacility) {
    updatedFacility.AllowProfessionalCoding = payload.allowProfessionalCoding;
  }

  selectedAccount.roles = newRoles;
  return {
    ...state,
    selectedAccount,
  };
};

const handleOptimisticSetAllProFee = (
  state: ManageEncoderUserState,
  payload: { userId: string; allowProfessionalCoding: boolean },
): ManageEncoderUserState => {
  const selectedAccount = { ...state.selectedAccount };

  selectedAccount.roles = selectedAccount.roles.map((el: FacilityRolesEntity) => ({
    ...el,
    AllowProfessionalCoding: payload.allowProfessionalCoding,
  }));

  return {
    ...state,
    selectedAccount,
  };
};

const handleUpdateUserFromApiResponse = (
  state: ManageEncoderUserState,
  payload: EncoderUserEntity,
): ManageEncoderUserState => {
  const selectedAccount = {
    ...state.selectedAccount,
    ...payload,
    // Keep existing roles since we're only updating basic user info
    roles: state.selectedAccount.roles,
    practiceRoles: state.selectedAccount.practiceRoles,
  };

  return {
    ...state,
    selectedAccount,
    loading: false,
    dirty: false,
  };
};

const handleSaveUserInfoError = (state: ManageEncoderUserState): ManageEncoderUserState => ({
  ...state,
  loading: false,
  errorText: "Failed to update user information",
});

const handleSetUserInfoDirty = (
  state: ManageEncoderUserState,
  payload: boolean,
): ManageEncoderUserState => ({
  ...state,
  userInfoDirty: payload,
});

const handleAdminSettingOptimisticUpdate = (
  state: ManageEncoderUserState,
  payload: { fieldName: string; value: boolean },
): ManageEncoderUserState => {
  const selectedAccount = {
    ...state.selectedAccount,
    [payload.fieldName]: payload.value,
  };

  return {
    ...state,
    selectedAccount,
    dirty: true,
  };
};

const handleAdminSettingRevert = (
  state: ManageEncoderUserState,
  payload: { fieldName: string; originalValue: boolean },
): ManageEncoderUserState => {
  const selectedAccount = {
    ...state.selectedAccount,
    [payload.fieldName]: payload.originalValue,
  };

  return {
    ...state,
    selectedAccount,
    dirty: false,
  };
};
